import clsx from 'clsx';
import { ReactElement } from 'react';
import { GasStorageDataType } from './types';

interface GasStorageVisualizationProps {
  data?: GasStorageDataType;
  motionless?: boolean;
}

export function GasStorageVisualization({
  data = {},
  motionless = false,
}: Readonly<GasStorageVisualizationProps>): ReactElement<GasStorageVisualizationProps> {
  const limitsAreValid =
    data.upper_soc_limit != null &&
    data.lower_soc_limit != null &&
    data.upper_soc_limit > data.lower_soc_limit &&
    data.upper_soc_limit >= 0 &&
    data.upper_soc_limit <= 1 &&
    data.lower_soc_limit >= 0 &&
    data.lower_soc_limit <= 1;

  return (
    <div aria-hidden className="w-3/4 h-full flex items-center justify-center box-border p-2">
      <div className="relative max-w-64 w-full aspect-[1.5] flex justify-center items-end box-border after:[content:''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:border-solid after:border-2 after:rounded-full">
        {/* Gas Storage */}
        <div className="absolute w-3/4 h-full box-border flex flex-col">
          <div className="w-full box-border border-solid border-0 border-b-4 flex-grow">
            <div className="relative w-full h-full box-border [clip-path:ellipse(50%_100%_at_50%_100%)] bg-black overflow-hidden">
              <div
                className={clsx(
                  'absolute top-1 bottom-0 left-1 right-1 box-border [clip-path:ellipse(50%_100%_at_50%_100%)] transition-colors duration-300 motion-reduce:transition-none',
                  data.upper_soc_limit && data.upper_soc_limit > 1 ? 'bg-error-500' : 'bg-white',
                )}
              />
            </div>
          </div>
          <div className="w-full h-3/4 box-border border-solid border-4 border-y-0 bg-white">
            <div
              className={clsx(
                'w-full h-full transition-all duration-300 motion-reduce:transition-none',
                data.upper_soc_limit && data.upper_soc_limit <= 1 ? 'bg-green-200' : 'bg-error-500',
              )}
              style={{
                transform: `scaleY(${data.upper_soc_limit ? Math.max(Math.min(data.upper_soc_limit, 1), 0) : 0})`,
                transformOrigin: 'bottom',
              }}
            />
          </div>

          {/* Legend */}
          {!!data.upper_soc_limit &&
            !Number.isNaN(data.upper_soc_limit) &&
            data.lower_soc_limit != null &&
            !Number.isNaN(data.lower_soc_limit) &&
            data.upper_soc_limit > data.lower_soc_limit &&
            data.upper_soc_limit >= 0 &&
            data.lower_soc_limit >= 0 && (
              <div className="absolute -right-[20%] bottom-0 h-3/4 [container:legend_/_size] flex items-end">
                <div
                  className={clsx(
                    'relative h-full flex flex-col items-center justify-center *:absolute *:duration-300 motion-reduce:*:transition-none',
                    { '*:transition-none': motionless },
                  )}
                >
                  <div
                    className={clsx(
                      '-bottom-3 w-3 aspect-square [clip-path:polygon(50%_0%,_0%_100%,_100%_100%)] transition-transform',
                      limitsAreValid ? 'bg-black' : 'bg-red-200',
                    )}
                    style={{
                      transform: `translateY(${Math.min(data.upper_soc_limit, 1) * -100}cqh)`,
                      transformOrigin: 'bottom',
                    }}
                  />
                  <div
                    className={clsx(
                      'top-0 bottom-0 box-border border-solid border-2 transition-transform',
                      limitsAreValid ? 'border-black' : 'border-red-200',
                    )}
                    style={{
                      transform: `translateY(${Math.min(data.lower_soc_limit, 1) * -100 - 6}cqh) scaleY(${Math.min(data.upper_soc_limit, 1) - data.lower_soc_limit - 0.12})`,
                      transformOrigin: 'bottom',
                    }}
                  />
                  <div
                    className={clsx(
                      'bottom-0 w-3 aspect-square [clip-path:polygon(50%_100%,_0%_1%,_100%_0%)] transition-transform',
                      limitsAreValid ? 'bg-black' : 'bg-red-200',
                    )}
                    style={{
                      transform: `translateY(${Math.min(data.lower_soc_limit, 1) * -100}cqh)`,
                      transformOrigin: 'bottom',
                    }}
                  />
                  <div
                    className={clsx('left-3 -bottom-2 font-medium whitespace-nowrap transition-all', {
                      'text-error-500': !limitsAreValid,
                    })}
                    style={{
                      transform: `translateY(${((Math.min(data.upper_soc_limit, 1) + data.lower_soc_limit) / 2) * -100}cqh)`,
                      transformOrigin: 'bottom',
                    }}
                  >
                    {`${Math.round((data.upper_soc_limit - data.lower_soc_limit) * 100)} %`}
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
